import { z } from "zod";
import { actorIdentity, actorIdentitySchema, requireCapability, requireTenant, } from "./authority.js";
import { canonicalJsonSchema, canonicalSha256, createProtocolSignature, } from "./canonicalize.js";
import { addCompletionDefinitionZodIssues, assertCompletionDefinition, canonicalProtocolIdSetSchema, completionIntentSchema, completionObligationClassificationSchema, nonemptyCanonicalProtocolIdSetSchema, } from "./completion.js";
import { UNIVERSAL_VERIFICATION_PROTOCOL, consequenceClassSchema, contractBindingSchema, evaluatorAuthoritySchema, protocolIdSchema, protocolTimestampSchema, protocolVersionIdSchema, protocolVersionSchema, sha256DigestSchema, signatureSchema, uriSchema, } from "./identifiers.js";
export const contractSourceSchema = z
    .object({
    id: protocolIdSchema,
    kind: z.enum([
        "organization_policy",
        "repository_policy",
        "repository_map",
        "work_request",
        "template",
        "human",
        "compiler",
        "worker_proposal",
        "external",
    ]),
    uri: uriSchema,
    digest: sha256DigestSchema,
    precedence: z.number().int().nonnegative(),
})
    .strict();
export const evaluatorBindingSchema = z
    .object({
    ref: protocolIdSchema,
    version: protocolVersionIdSchema,
    digest: sha256DigestSchema,
    authority: evaluatorAuthoritySchema,
    rubricDigest: sha256DigestSchema.nullable(),
    requiresIndependentExecution: z.boolean(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.authority === "semantic" && value.rubricDigest === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["rubricDigest"],
            message: "semantic evaluators require a pinned rubric digest",
        });
    }
});
export const oracleBindingSchema = z
    .object({
    ref: protocolIdSchema,
    version: protocolVersionIdSchema,
    digest: sha256DigestSchema,
    required: z.boolean(),
})
    .strict();
export const evidenceRequirementSchema = z
    .object({
    kind: protocolIdSchema,
    minimumCount: z.number().int().positive(),
    maximumAgeSeconds: z.number().int().nonnegative().nullable(),
    requiresIndependentCollection: z.boolean(),
})
    .strict();
/** Host-admitted repository identity frozen into repository-backed contracts. */
export const repositorySelectionBindingSchema = z
    .object({
    schemaId: z.literal("excellent.repository-selection-binding.v1"),
    selectionId: protocolIdSchema,
    repositoryId: protocolIdSchema,
    admissionRevision: z.string().trim().min(1).max(200),
    hostAdmissionDigest: sha256DigestSchema,
    snapshotDigest: sha256DigestSchema,
    witness: z
        .object({
        path: z.string().trim().min(1),
        digest: sha256DigestSchema,
    })
        .strict(),
    bindingDigest: sha256DigestSchema,
})
    .strict();
export function repositorySelectionBindingDigest(binding, sha256) {
    return canonicalSha256(binding, sha256);
}
const contractObligationCommonShape = {
    id: protocolIdSchema,
    subject: protocolIdSchema,
    predicate: z
        .object({
        kind: protocolIdSchema,
        description: z.string().trim().min(1),
        parameters: z.record(canonicalJsonSchema),
    })
        .strict(),
    successCondition: z.string().trim().min(1).nullable(),
    falsifier: z.string().trim().min(1).nullable(),
    required: z.boolean(),
    evidence: z.array(evidenceRequirementSchema).min(1),
    evaluator: evaluatorBindingSchema,
    oracles: z.array(oracleBindingSchema),
};
function addContractObligationIssues(value, context) {
    if (value.successCondition === null && value.falsifier === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["successCondition"],
            message: "an obligation requires a success condition or falsifier",
        });
    }
}
export const contractObligationSchema = z
    .object({
    ...contractObligationCommonShape,
    classification: completionObligationClassificationSchema,
    sourceIds: nonemptyCanonicalProtocolIdSetSchema,
    coversOutcomeIds: canonicalProtocolIdSetSchema,
})
    .strict()
    .superRefine(addContractObligationIssues);
export const legacyContractObligationSchema = z
    .object(contractObligationCommonShape)
    .strict()
    .superRefine(addContractObligationIssues);
const contractContentCommonShape = {
    protocolVersion: protocolVersionSchema,
    contractId: protocolIdSchema,
    version: protocolVersionIdSchema,
    supersedesContractDigest: sha256DigestSchema.nullable(),
    tenantId: protocolIdSchema,
    projectId: protocolIdSchema.nullable(),
    workItemId: protocolIdSchema,
    workType: protocolIdSchema,
    title: z.string().trim().min(1),
    requester: actorIdentitySchema,
    repositoryBinding: repositorySelectionBindingSchema.optional(),
    sources: z.array(contractSourceSchema).min(1),
    bounds: z
        .object({
        allowedResources: z.array(z.string().min(1)),
        forbiddenResources: z.array(z.string().min(1)),
        allowedMutations: z.array(z.string().min(1)),
        network: z.enum(["denied", "allowlisted", "unrestricted"]),
    })
        .strict(),
    aggregation: z
        .object({
        mode: z.literal("fail_closed"),
        optionalFailuresAffectAggregate: z.boolean(),
    })
        .strict(),
    unknownPolicy: z
        .object({
        action: z.enum(["HOLD", "RETURN", "NEEDS_HUMAN"]),
        notRunAction: z.enum(["RETURN", "RETRY", "NEEDS_HUMAN"]),
    })
        .strict(),
    consequence: z
        .object({
        class: consequenceClassSchema,
        enforcementPolicyRef: protocolIdSchema,
        requiresProspectiveAssurance: z.boolean(),
    })
        .strict(),
    privacy: z
        .object({
        retentionPolicyRef: protocolIdSchema,
        disclosurePolicyRef: protocolIdSchema,
        secretCollection: z.literal("deny_by_default"),
    })
        .strict(),
};
function addContractContentCommonIssues(value, context) {
    if (value.requester.tenantId !== value.tenantId) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["requester", "tenantId"],
            message: "requester tenant must match contract tenant",
        });
    }
    const obligationIds = value.obligations.map((obligation) => obligation.id);
    if (new Set(obligationIds).size !== obligationIds.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["obligations"],
            message: "obligation IDs must be unique",
        });
    }
    const sourceIds = value.sources.map((source) => source.id);
    if (new Set(sourceIds).size !== sourceIds.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["sources"],
            message: "contract source IDs must be unique",
        });
    }
}
export const contractContentSchema = z
    .object({
    ...contractContentCommonShape,
    completionIntent: completionIntentSchema,
    obligations: z.array(contractObligationSchema).min(1),
})
    .strict()
    .superRefine((value, context) => {
    addContractContentCommonIssues(value, context);
    addCompletionDefinitionZodIssues(value, context);
});
export const legacyContractContentSchema = z
    .object({
    ...contractContentCommonShape,
    obligations: z.array(legacyContractObligationSchema).min(1),
})
    .strict()
    .superRefine(addContractContentCommonIssues);
export function parseContractContent(input) {
    assertCompletionDefinition(input);
    return contractContentSchema.parse(input);
}
export const contractCandidateSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    content: contractContentSchema,
})
    .strict();
export const compilationDecisionSchema = z
    .object({
    obligationId: protocolIdSchema,
    decision: z.enum(["admitted", "rejected", "human_required"]),
    reasonCodes: z.array(protocolIdSchema),
})
    .strict();
export const compilationRepositoryContextSchema = z
    .object({
    repositoryId: protocolIdSchema,
    mapDigest: sha256DigestSchema,
    repositoryShape: z.enum([
        "monorepo",
        "single_package",
        "polyglot",
        "make_only",
        "ci_only",
        "no_test",
        "nested_worktree",
        "unsupported",
    ]),
    selectedCommandIds: z.array(protocolIdSchema),
    preservedSafetyGateIds: z.array(protocolIdSchema),
    coverageBoundary: z.string().trim().min(1).nullable(),
})
    .strict();
// Keep downstream generated API declaration types bounded without relaxing the
// runtime object's strict schema or any of its identifier/digest constraints.
export const compilationSourceReviewSchema = z.object({
    schema: z.literal("excellent.source-review-provenance.v1"),
    sourceTenantId: protocolIdSchema,
    sourceActorId: protocolIdSchema,
    scenario: protocolIdSchema,
    authorityBindingDigest: sha256DigestSchema,
    intakeId: protocolIdSchema,
    compilationId: protocolIdSchema,
    compilationDigest: sha256DigestSchema,
    reviewId: protocolIdSchema,
    reviewDigest: sha256DigestSchema,
    candidateContractDigest: sha256DigestSchema,
    canonicalReviewer: actorIdentitySchema,
}).strict();
export const compilationRecordContentSchema = z
    .object({
    id: protocolIdSchema,
    compiler: actorIdentitySchema,
    compilerVersion: protocolVersionIdSchema,
    compiledAt: protocolTimestampSchema,
    sourceDigests: z.array(sha256DigestSchema).min(1),
    candidateDigest: sha256DigestSchema,
    admittedContractDigest: sha256DigestSchema,
    repositoryContext: compilationRepositoryContextSchema.nullable().optional(),
    sourceReview: compilationSourceReviewSchema.optional(),
    decisions: z.array(compilationDecisionSchema).min(1),
})
    .strict();
export const compilationRecordSchema = z
    .object({
    content: compilationRecordContentSchema,
    digest: sha256DigestSchema,
})
    .strict();
export const contractAcceptanceSchema = z
    .object({
    acceptedBy: actorIdentitySchema,
    acceptedAt: protocolTimestampSchema,
    authorityBasis: z.enum(["requester", "policy", "named_human"]),
    contractDigest: sha256DigestSchema,
    compilationRecordDigest: sha256DigestSchema,
    payloadDigest: sha256DigestSchema,
    signature: signatureSchema,
})
    .strict();
function addAcceptedContractEnvelopeIssues(value, context) {
    const sourceReview = value.compilationRecord.content.sourceReview;
    if (sourceReview && (sourceReview.candidateContractDigest !== value.contractDigest ||
        sourceReview.canonicalReviewer.tenantId !== value.content.tenantId ||
        JSON.stringify(sourceReview.canonicalReviewer) !== JSON.stringify(value.acceptance.acceptedBy))) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["compilationRecord", "content", "sourceReview"],
            message: "source review must bind the exact accepted contract and accepter identity" });
    }
    if (value.compilationRecord.content.admittedContractDigest !== value.contractDigest) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["compilationRecord", "content", "admittedContractDigest"],
            message: "compilation record must bind the accepted contract digest",
        });
    }
    if (value.compilationRecord.content.candidateDigest !== value.contractDigest) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["compilationRecord", "content", "candidateDigest"],
            message: "accepted candidate digest must equal the admitted contract digest",
        });
    }
    if (value.acceptance.contractDigest !== value.contractDigest ||
        value.acceptance.compilationRecordDigest !== value.compilationRecord.digest) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["acceptance"],
            message: "acceptance must bind contract and compilation record digests",
        });
    }
    if (value.compilationRecord.content.compiler.tenantId !== value.content.tenantId) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["compilationRecord", "content", "compiler", "tenantId"],
            message: "compiler tenant must match the accepted contract tenant",
        });
    }
    const expectedSourceDigests = value.content.sources.map((source) => source.digest).sort();
    const compiledSourceDigests = [...value.compilationRecord.content.sourceDigests].sort();
    if (expectedSourceDigests.length !== compiledSourceDigests.length ||
        expectedSourceDigests.some((digest, index) => digest !== compiledSourceDigests[index])) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["compilationRecord", "content", "sourceDigests"],
            message: "compilation source digests must exactly match contract sources",
        });
    }
    const decisions = value.compilationRecord.content.decisions;
    const decisionIds = decisions.map((decision) => decision.obligationId);
    if (new Set(decisionIds).size !== decisionIds.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["compilationRecord", "content", "decisions"],
            message: "compilation decisions must be unique per obligation",
        });
    }
    for (const obligation of value.content.obligations) {
        const decision = decisions.find((candidate) => candidate.obligationId === obligation.id);
        if (decision?.decision !== "admitted") {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["compilationRecord", "content", "decisions"],
                message: `accepted obligation ${obligation.id} requires an admitted compiler decision`,
            });
        }
    }
    if (value.acceptance.authorityBasis === "requester" &&
        value.acceptance.acceptedBy.actorId !== value.content.requester.actorId) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["acceptance", "acceptedBy", "actorId"],
            message: "requester acceptance must be signed by the named requester",
        });
    }
}
const acceptedContractEnvelopeCommonShape = {
    protocolVersion: protocolVersionSchema,
    contractDigest: sha256DigestSchema,
    compilationRecord: compilationRecordSchema,
    acceptance: contractAcceptanceSchema,
    lifecycle: z.literal("ACCEPTED"),
    deploymentMode: z.enum(["REPLAY", "SHADOW", "REVIEW_GATED", "ENFORCED", "PAUSED"]),
};
export const currentAcceptedContractEnvelopeSchema = z
    .object({
    ...acceptedContractEnvelopeCommonShape,
    content: contractContentSchema,
})
    .strict()
    .superRefine(addAcceptedContractEnvelopeIssues);
export const legacyAcceptedContractEnvelopeSchema = z
    .object({
    ...acceptedContractEnvelopeCommonShape,
    content: legacyContractContentSchema,
})
    .strict()
    .superRefine(addAcceptedContractEnvelopeIssues);
export const acceptedContractEnvelopeSchema = z.union([
    currentAcceptedContractEnvelopeSchema,
    legacyAcceptedContractEnvelopeSchema,
]);
export function isCurrentAcceptedContractEnvelope(contract) {
    return currentAcceptedContractEnvelopeSchema.safeParse(contract).success;
}
export function assertCurrentAcceptedContractEnvelope(input) {
    const contract = acceptedContractEnvelopeSchema.parse(input);
    assertCompletionDefinition(contract.content);
    return currentAcceptedContractEnvelopeSchema.parse(contract);
}
export const contractAmendmentSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    tenantId: protocolIdSchema,
    workItemId: protocolIdSchema,
    baseContractDigest: sha256DigestSchema,
    proposedBy: actorIdentitySchema,
    proposedAt: protocolTimestampSchema,
    proposedDiff: canonicalJsonSchema,
    proposedContent: contractContentSchema,
    proposedContentDigest: sha256DigestSchema,
    rationale: z.string().trim().min(1),
    impactedObligationIds: z.array(protocolIdSchema),
    // SUBMITTED by the amendment proposer, not derived by the engine, so it
    // stays the four statuses a party may assert. `MISMATCH` is excluded for
    // the same reason `workerFinalClaimSchema` excludes it: a proposer cannot
    // author an accusation. Kept byte-for-byte in lockstep with the request
    // schema in `worker-amendments.ts`.
    caseImpact: z
        .object({
        status: z.enum(["PASS", "FAIL", "UNKNOWN", "NOT_RUN"]),
        digest: sha256DigestSchema,
        reasonCodes: z.array(protocolIdSchema),
    })
        .strict(),
    material: z.boolean(),
    state: z.literal("PENDING"),
})
    .strict()
    .superRefine((value, context) => {
    if (value.proposedContent.tenantId !== value.tenantId ||
        value.proposedContent.workItemId !== value.workItemId) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["proposedContent"],
            message: "amendment content must remain in the same tenant and work item",
        });
    }
    if (value.proposedContent.supersedesContractDigest !== value.baseContractDigest) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["proposedContent", "supersedesContractDigest"],
            message: "amendment content must supersede its base contract digest",
        });
    }
});
export const contractAmendmentDecisionSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    amendmentId: protocolIdSchema,
    tenantId: protocolIdSchema,
    workItemId: protocolIdSchema,
    baseContractDigest: sha256DigestSchema,
    proposedContentDigest: sha256DigestSchema,
    decision: z.enum(["ACCEPTED", "REJECTED"]),
    decidedBy: actorIdentitySchema,
    decidedAt: protocolTimestampSchema,
    authorityBasis: z.enum(["requester", "policy", "named_human"]),
    reason: z.string().trim().min(1),
    successorContractDigest: sha256DigestSchema.nullable(),
    replacementAttemptId: protocolIdSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.decision === "ACCEPTED" && value.successorContractDigest === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["successorContractDigest"],
            message: "accepted amendments require a successor contract digest",
        });
    }
    if (value.decision === "REJECTED" &&
        (value.successorContractDigest !== null || value.replacementAttemptId !== null)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["decision"],
            message: "rejected amendments cannot create successor state",
        });
    }
});
export function proposeContractAmendment(input) {
    const base = assertCurrentAcceptedContractEnvelope(input.baseContract);
    const content = parseContractContent(input.proposedContent);
    requireTenant(input.proposer, base.content.tenantId);
    requireCapability(input.proposer, "contract:amend:propose");
    if (content.contractId !== base.content.contractId) {
        throw new Error("amendment cannot change the stable contract ID");
    }
    return contractAmendmentSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        tenantId: base.content.tenantId,
        workItemId: base.content.workItemId,
        baseContractDigest: base.contractDigest,
        proposedBy: actorIdentity(input.proposer),
        proposedAt: input.proposedAt,
        proposedDiff: input.proposedDiff,
        proposedContent: content,
        proposedContentDigest: contractDigest(content, input.sha256),
        rationale: input.rationale,
        impactedObligationIds: input.impactedObligationIds,
        caseImpact: input.caseImpact,
        material: input.material,
        state: "PENDING",
    });
}
export function decideContractAmendment(input) {
    const amendment = contractAmendmentSchema.parse(input.amendment);
    requireTenant(input.actor, amendment.tenantId);
    requireCapability(input.actor, "contract:amend:decide");
    requireCapability(input.actor, "contract:accept");
    if (input.actor.actorId === input.workerActorId) {
        throw new Error("the worker cannot decide its own contract amendment");
    }
    if (amendment.proposedBy.actorId === input.actor.actorId &&
        amendment.proposedBy.roles.includes("worker")) {
        throw new Error("a worker proposal requires independent amendment authority");
    }
    return contractAmendmentDecisionSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        amendmentId: amendment.id,
        tenantId: amendment.tenantId,
        workItemId: amendment.workItemId,
        baseContractDigest: amendment.baseContractDigest,
        proposedContentDigest: amendment.proposedContentDigest,
        decision: input.decision,
        decidedBy: actorIdentity(input.actor),
        decidedAt: input.decidedAt,
        authorityBasis: input.authorityBasis,
        reason: input.reason,
        successorContractDigest: input.successorContractDigest ?? null,
        replacementAttemptId: input.replacementAttemptId ?? null,
    });
}
export function contractBinding(contract) {
    return contractBindingSchema.parse({
        tenantId: contract.content.tenantId,
        workItemId: contract.content.workItemId,
        contractId: contract.content.contractId,
        contractVersion: contract.content.version,
        contractDigest: contract.contractDigest,
    });
}
export function contractDigest(content, sha256) {
    return canonicalSha256(parseContractContent(content), sha256);
}
export async function createAcceptedContract(input) {
    const content = parseContractContent(input.content);
    requireTenant(input.compilerActor, content.tenantId);
    requireCapability(input.compilerActor, "contract:compile");
    requireTenant(input.accepter, content.tenantId);
    requireCapability(input.accepter, "contract:accept");
    const authenticatedCompiler = actorIdentity(input.compilerActor);
    if (authenticatedCompiler.actorId !== input.compilation.compiler.actorId ||
        authenticatedCompiler.tenantId !== input.compilation.compiler.tenantId ||
        authenticatedCompiler.executionBoundaryId !== input.compilation.compiler.executionBoundaryId) {
        throw new Error("compilation record does not match its authenticated compiler");
    }
    const digest = contractDigest(content, input.sha256);
    const compilationContent = compilationRecordContentSchema.parse({
        ...input.compilation,
        admittedContractDigest: digest,
    });
    const compilationDigest = canonicalSha256(compilationContent, input.sha256);
    const acceptancePayload = {
        acceptedBy: actorIdentity(input.accepter),
        acceptedAt: input.acceptedAt,
        authorityBasis: input.authorityBasis,
        contractDigest: digest,
        compilationRecordDigest: compilationDigest,
    };
    const acceptancePayloadDigest = canonicalSha256(acceptancePayload, input.sha256);
    const acceptance = contractAcceptanceSchema.parse({
        ...acceptancePayload,
        payloadDigest: acceptancePayloadDigest,
        signature: await createProtocolSignature(acceptancePayloadDigest, input.signer),
    });
    return currentAcceptedContractEnvelopeSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        content,
        contractDigest: digest,
        compilationRecord: {
            content: compilationContent,
            digest: compilationDigest,
        },
        acceptance,
        lifecycle: "ACCEPTED",
        deploymentMode: input.deploymentMode,
    });
}
export function verifyAcceptedContractDigests(contract, sha256) {
    const parsed = acceptedContractEnvelopeSchema.parse(contract);
    const currentContent = contractContentSchema.safeParse(parsed.content);
    const canonicalContent = currentContent.success
        ? currentContent.data
        : legacyContractContentSchema.parse(parsed.content);
    const contentMatches = canonicalSha256(canonicalContent, sha256) === parsed.contractDigest;
    const compilationMatches = canonicalSha256(parsed.compilationRecord.content, sha256) ===
        parsed.compilationRecord.digest;
    const acceptancePayload = {
        acceptedBy: parsed.acceptance.acceptedBy,
        acceptedAt: parsed.acceptance.acceptedAt,
        authorityBasis: parsed.acceptance.authorityBasis,
        contractDigest: parsed.acceptance.contractDigest,
        compilationRecordDigest: parsed.acceptance.compilationRecordDigest,
    };
    const acceptanceMatches = canonicalSha256(acceptancePayload, sha256) === parsed.acceptance.payloadDigest;
    return contentMatches && compilationMatches && acceptanceMatches;
}
export function assuranceForContract(contract, attemptOpenedAt) {
    if (!("acceptance" in contract))
        return "RETROSPECTIVE";
    const current = currentAcceptedContractEnvelopeSchema.parse(contract);
    return Date.parse(current.acceptance.acceptedAt) <= Date.parse(attemptOpenedAt)
        ? "PROSPECTIVE"
        : "RETROSPECTIVE";
}
